import { Link } from "react-router-dom";
import { PublicLayout } from "../../components/public-layout.js";
import {
  HeroSection,
  TrustCallout,
  CTASection,
} from "../../components/marketing-components.js";

export function MethodologyPage() {
  return (
    <PublicLayout>
      <HeroSection
        title="Our methodology"
        subtitle="How we scan. What we find. What you still need to do."
      />

      <section className="marketing-section">
        <div className="section-content">
          <TrustCallout>
            <h3>Our commitment</h3>
            <p>
              We're transparent about what automated testing can and cannot do.
              Accessibility is complex. We help you find real issues, but we
              don't claim to replace manual review or legal consultation.
            </p>
          </TrustCallout>
        </div>

        <div className="section-content mt-4">
          <h2>The scanning process</h2>

          <div className="methodology-section">
            <h3>1. Rendering</h3>
            <p>
              We use a real browser (Playwright) to load your pages, not just
              parse HTML. This means we can test JavaScript-rendered content,
              focus states, and dynamic changes.
            </p>
          </div>

          <div className="methodology-section">
            <h3>2. Template detection</h3>
            <p>
              You tell us about your site structure—homepages, product pages,
              forms, etc. We use this to crawl intelligently and group findings
              by page type, not just by URL.
            </p>
          </div>

          <div className="methodology-section">
            <h3>3. Multiple engines</h3>
            <p>We run three industry-standard engines on each page:</p>
            <ul>
              <li>
                <strong>Axe:</strong> Deque's axe-core, catches contrast, ARIA,
                heading structure
              </li>
              <li>
                <strong>Pa11y:</strong> HTML_CodeSniffer-based ruleset for
                forms, links, landmarks
              </li>
              <li>
                <strong>Custom checks:</strong> Site-specific patterns (e.g.,
                custom components)
              </li>
            </ul>
          </div>

          <div className="methodology-section">
            <h3>4. Grouping and deduplication</h3>
            <p>
              We deduplicate findings across engines and group by: impact level,
              rule violated, and page type. This prevents overwhelm and focuses
              remediation.
            </p>
          </div>

          <div className="methodology-section">
            <h3>5. Evidence capture</h3>
            <p>
              Each finding includes: screenshot of the problem, HTML snippet,
              CSS context, and remediation guidance. Context matters.
            </p>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>What automated testing finds well</h2>

          <div className="checklist">
            <div className="checklist-item">
              <span className="checklist-icon">✓</span>
              <div>
                <h4>Color contrast</h4>
                <p>
                  We measure actual foreground/background contrast ratios. WCAG
                  AA/AAA compliance is verifiable.
                </p>
              </div>
            </div>

            <div className="checklist-item">
              <span className="checklist-icon">✓</span>
              <div>
                <h4>Alt text</h4>
                <p>
                  Missing alt attributes, empty alt, placeholder text. We flag
                  all of these. But we can't judge if alt text is *good*.
                </p>
              </div>
            </div>

            <div className="checklist-item">
              <span className="checklist-icon">✓</span>
              <div>
                <h4>Keyboard navigation</h4>
                <p>
                  Tab order, focus visible, trapped focus. We can detect most
                  keyboard issues without screen reader testing.
                </p>
              </div>
            </div>

            <div className="checklist-item">
              <span className="checklist-icon">✓</span>
              <div>
                <h4>Form labeling</h4>
                <p>
                  Missing labels, incorrect associations, aria-labels. Most
                  labeling issues are caught.
                </p>
              </div>
            </div>

            <div className="checklist-item">
              <span className="checklist-icon">✓</span>
              <div>
                <h4>Heading hierarchy</h4>
                <p>
                  Skipped levels, missing h1, structural issues. Clear patterns
                  are detectable.
                </p>
              </div>
            </div>

            <div className="checklist-item">
              <span className="checklist-icon">✓</span>
              <div>
                <h4>ARIA misuse</h4>
                <p>
                  Invalid ARIA attributes, redundant roles, conflicting
                  expectations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>What requires manual testing</h2>

          <div className="warning-box">
            <h3>These need real human or assistive-tech testing:</h3>

            <div className="manual-list">
              <div className="manual-item">
                <h4>Screen reader experience</h4>
                <p>
                  Automated tools can't hear what NVDA or JAWS users hear.
                  Custom widgets, dynamic updates, and announcement patterns all
                  need real testing.
                </p>
              </div>

              <div className="manual-item">
                <h4>Mobile accessibility</h4>
                <p>
                  Touch targets, zoom behavior, mobile screen readers.
                  Mobile-specific issues are invisible to desktop automation.
                </p>
              </div>

              <div className="manual-item">
                <h4>Content quality</h4>
                <p>
                  Is alt text actually *good*? Is your link text clear? Is your
                  form error messaging helpful? These are human judgment calls.
                </p>
              </div>

              <div className="manual-item">
                <h4>Complex interactions</h4>
                <p>
                  Multi-step forms, shopping carts, drag-and-drop, customizable
                  dashboards. Edge cases and workflows need real user testing.
                </p>
              </div>

              <div className="manual-item">
                <h4>Cognitive accessibility</h4>
                <p>
                  Plain language, information architecture, task clarity. These
                  require human review and user feedback.
                </p>
              </div>

              <div className="manual-item">
                <h4>Video and multimedia</h4>
                <p>
                  We can detect missing captions, but we can't judge caption
                  *quality* or whether transcripts are accurate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Confidence and truthfulness</h2>

          <p>
            When we report an issue, we show you the confidence level: high,
            medium, or low.
          </p>

          <div className="confidence-scale">
            <div className="confidence-item">
              <h4>High confidence</h4>
              <p>
                Binary, measurable: missing alt, form label missing, color ratio
                fails. These are real issues.
              </p>
            </div>

            <div className="confidence-item">
              <h4>Medium confidence</h4>
              <p>
                Pattern-based: heading hierarchy skips, focus trap detected,
                ARIA misuse. Usually correct, but context matters.
              </p>
            </div>

            <div className="confidence-item">
              <h4>Low confidence</h4>
              <p>
                Heuristic-based: possible empty button, possible link confusion.
                Needs human verification.
              </p>
            </div>
          </div>

          <p className="mt-4">
            <strong>We never claim full coverage.</strong> Our scans find many
            issues, but not all. We tell you what we tested and what still needs
            manual review.
          </p>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>Document scanning</h2>

          <p>
            PDFs, Word docs, and spreadsheets are scanned separately from your
            website because they have different accessibility requirements.
          </p>

          <div className="doc-approach">
            <div className="doc-item">
              <h4>Detection</h4>
              <p>
                We crawl your site and find downloadable documents. You can
                opt-in to scanning.
              </p>
            </div>

            <div className="doc-item">
              <h4>PDF analysis</h4>
              <p>
                For PDFs, we use both automated tools and manual review to
                check: text extraction, alternative text, structural tagging,
                form accessibility.
              </p>
            </div>

            <div className="doc-item">
              <h4>Separate scoring</h4>
              <p>
                Document accessibility is scored separately from your website.
                You generate a separate remediation statement for documents.
              </p>
            </div>

            <div className="doc-item">
              <h4>Limitations</h4>
              <p>
                PDFs with heavy graphics, image-based scans, and non-Latin text
                may need manual review.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Recurring monitoring</h2>

          <p>
            A single audit is a snapshot. Our monthly scans track how your
            accessibility evolves.
          </p>

          <div className="recurring-benefits">
            <div>
              <h4>New issues detected</h4>
              <p>
                When templates change or new pages launch, new issues appear. We
                flag them immediately.
              </p>
            </div>

            <div>
              <h4>Progress visible</h4>
              <p>
                See which issues you've fixed, which are persisting, and which
                are newly introduced.
              </p>
            </div>

            <div>
              <h4>Proof of commitment</h4>
              <p>
                Monthly reports demonstrate that you're actively monitoring and
                remediating accessibility issues.
              </p>
            </div>

            <div>
              <h4>Trend analysis</h4>
              <p>
                Over time, you see trends: is accessibility getting better or
                worse? Are remediations sticking?
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>Not a legal substitute</h2>

          <TrustCallout>
            <p>
              <strong>We cannot provide legal compliance guarantees.</strong>{" "}
              Accessibility law is evolving (ADA, AODA, WCAG, EN standards vary
              by region). We help you find and remediate issues, but you should
              consult legal counsel for your specific context.
            </p>

            <p>
              Our reports and statements are factual summaries of what we found.
              They're useful for remediation planning and demonstrating good
              faith effort, but they're not legal opinions.
            </p>
          </TrustCallout>
        </div>
      </section>

      <CTASection
        title="Learn more about manual review"
        description="For high-risk sites or legal compliance needs, pair our automated scanning with professional manual review."
        primaryCTA={{ label: "Manual review services", href: "/manual-review" }}
        secondaryCTA={{ label: "Contact us", href: "/contact" }}
      />
    </PublicLayout>
  );
}
